import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  MenuItem,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ArrowForward,
  CheckCircle,
  LockOutlined,
  PersonOutline,
} from '@mui/icons-material';
import apiClient from '../lib/axios';
import { PhoneInput } from '../components/PhoneInput';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useAuth } from '../contexts/AuthContext';

// ─── Design Tokens ───────────────────────────────────────────────────────────

const NAVY = '#0D1B35';
const BLUE = '#0066CC';
const GREEN = '#00A86B';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontSize: 14,
    bgcolor: '#FAFBFD',
    '& fieldset': { borderColor: '#E1E8ED', borderWidth: '1.5px' },
    '&:hover fieldset': { borderColor: '#C1C9D2' },
    '&.Mui-focused fieldset': { borderColor: BLUE, borderWidth: '2px' },
    '&.Mui-focused': { bgcolor: '#fff' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: BLUE },
};

// ─── Schemas ─────────────────────────────────────────────────────────────────

const passwordSchema = z.object({
  password: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Las contraseñas no coinciden',
  path: ['confirmPassword'],
});

const profileSchema = z.object({
  firstName: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  lastName: z.string().min(2, 'El apellido debe tener al menos 2 caracteres'),
  phoneNumber: z.string().refine((val) => !val || isValidPhoneNumber(val), { message: 'Número de teléfono inválido' }),
  specialtyId: z.string().optional().or(z.literal('')),
});

type PasswordForm = z.infer<typeof passwordSchema>;
type ProfileForm = z.infer<typeof profileSchema>;

// ─── Step configurations ─────────────────────────────────────────────────────

const STEPS = [
  { icon: <LockOutlined />, title: 'Seguridad', desc: 'Crea una contraseña segura para tu cuenta' },
  { icon: <PersonOutline />, title: 'Tu Perfil', desc: 'Cuéntanos sobre ti para personalizar tu experiencia' },
];

// ─── Main Component ──────────────────────────────────────────────────────────

export const Onboard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refetchUser } = useAuth();
  const token = searchParams.get('token') || '';

  const [step, setStep] = useState(0); // 0=validating, 1=password, 2=profile, 3=done
  const [passwordData, setPasswordData] = useState<{ password: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState(false);

  const { data: validation, isLoading: isValidating } = useQuery<{ valid: boolean; email?: string }>({
    queryKey: ['validate-invitation', token],
    queryFn: async () => {
      const { data } = await apiClient.get('/auth/onboard/validate', { params: { token } });
      return data;
    },
    enabled: !!token,
    retry: false,
  });

  useEffect(() => {
    if (validation?.valid) setStep(1);
  }, [validation]);

  const { data: specialties } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['onboard-specialties'],
    queryFn: async () => {
      const { data } = await apiClient.get('/auth/onboard/specialties');
      return data;
    },
    enabled: step >= 1,
  });

  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: { firstName: '', lastName: '', phoneNumber: '', specialtyId: '' },
  });

  const onboardMutation = useMutation({
    mutationFn: async (data: { password: string; firstName: string; lastName: string; phoneNumber: string; specialtyId?: string }) => {
      const { data: result } = await apiClient.post('/auth/onboard', { token, ...data });
      return result;
    },
    onSuccess: async (result) => {
      localStorage.setItem('accessToken', result.accessToken);
      await refetchUser();
      goToStep(3);
      setTimeout(() => navigate('/doctor/dashboard', { replace: true }), 1500);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Error al completar el registro');
    },
  });

  const goToStep = (next: number) => {
    setTransitioning(true);
    setTimeout(() => {
      setStep(next);
      setTransitioning(false);
    }, 300);
  };

  const handlePasswordSubmit = (data: PasswordForm) => {
    setPasswordData({ password: data.password });
    goToStep(2);
  };

  const handleProfileSubmit = (data: ProfileForm) => {
    if (!passwordData) return;
    setError(null);
    onboardMutation.mutate({
      password: passwordData.password,
      firstName: data.firstName,
      lastName: data.lastName,
      phoneNumber: data.phoneNumber,
      specialtyId: data.specialtyId || undefined,
    });
  };

  // ── Error states (standalone cards) ──

  if (!token) {
    return <ErrorCard message="Enlace de invitación inválido." />;
  }

  if (validation && !validation.valid) {
    return <ErrorCard message="Este enlace de invitación es inválido o ha expirado." sub="Contacta a tu administrador para solicitar una nueva invitación." />;
  }

  // ── Loading state ──

  if (isValidating || step === 0) {
    return (
      <OnboardShell activeStep={0}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, py: 8 }}>
          <CircularProgress sx={{ color: BLUE, mb: 2 }} />
          <Typography sx={{ color: '#A0AEC0', fontSize: 14 }}>Verificando invitación...</Typography>
        </Box>
      </OnboardShell>
    );
  }

  // ── Success state ──

  if (step === 3) {
    return (
      <OnboardShell activeStep={2}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            py: 8,
            animation: 'fadeUp 500ms ease both',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'rgba(0,168,107,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              animation: 'scaleIn 400ms ease both',
            }}
          >
            <CheckCircle sx={{ fontSize: 44, color: GREEN }} />
          </Box>
          <Typography variant="h3" sx={{ color: NAVY, mb: 1, textAlign: 'center' }}>
            Bienvenido a Lysi
          </Typography>
          <Typography sx={{ fontSize: 14, color: '#A0AEC0', textAlign: 'center' }}>
            Tu cuenta ha sido creada exitosamente
          </Typography>
          <Box sx={{ width: 40, height: 3, borderRadius: 2, background: `linear-gradient(90deg, ${BLUE}, ${GREEN})`, mt: 3 }} />
        </Box>
      </OnboardShell>
    );
  }

  // ── Step 1: Password ──

  if (step === 1) {
    return (
      <OnboardShell activeStep={0}>
        <FormPanel visible={!transitioning}>
          <Typography variant="h3" sx={{ color: NAVY, mb: 0.5 }}>
            Crea tu contraseña
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#A0AEC0', mb: 3 }}>
            {validation?.email}
          </Typography>

          <Box component="form" onSubmit={passwordForm.handleSubmit(handlePasswordSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Contraseña"
              type={showPassword ? 'text' : 'password'}
              error={!!passwordForm.formState.errors.password}
              helperText={passwordForm.formState.errors.password?.message}
              {...passwordForm.register('password')}
              sx={fieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirmar contraseña"
              type={showConfirm ? 'text' : 'password'}
              error={!!passwordForm.formState.errors.confirmPassword}
              helperText={passwordForm.formState.errors.confirmPassword?.message}
              {...passwordForm.register('confirmPassword')}
              sx={fieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowConfirm(!showConfirm)}>
                      {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              endIcon={<ArrowForward />}
              sx={{
                bgcolor: BLUE,
                borderRadius: '10px',
                py: 1.5,
                fontWeight: 700,
                fontSize: 14,
                textTransform: 'none',
                mt: 1,
                boxShadow: '0 4px 16px rgba(0,102,204,0.3)',
                '&:hover': { bgcolor: '#004C99', boxShadow: '0 6px 22px rgba(0,102,204,0.42)' },
              }}
            >
              Siguiente
            </Button>
          </Box>
        </FormPanel>
      </OnboardShell>
    );
  }

  // ── Step 2: Profile ──

  return (
    <OnboardShell activeStep={1}>
      <FormPanel visible={!transitioning}>
        <Typography variant="h3" sx={{ color: NAVY, mb: 0.5 }}>
          Completa tu perfil
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#A0AEC0', mb: 3 }}>
          Información básica para tu cuenta
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={profileForm.handleSubmit(handleProfileSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              fullWidth
              label="Nombre"
              error={!!profileForm.formState.errors.firstName}
              helperText={profileForm.formState.errors.firstName?.message}
              {...profileForm.register('firstName')}
              sx={fieldSx}
            />
            <TextField
              fullWidth
              label="Apellido"
              error={!!profileForm.formState.errors.lastName}
              helperText={profileForm.formState.errors.lastName?.message}
              {...profileForm.register('lastName')}
              sx={fieldSx}
            />
          </Box>
          <Controller
            name="phoneNumber"
            control={profileForm.control}
            render={({ field }) => (
              <PhoneInput
                value={field.value}
                onChange={field.onChange}
                label="Teléfono"
                error={!!profileForm.formState.errors.phoneNumber}
                helperText={profileForm.formState.errors.phoneNumber?.message}
              />
            )}
          />
          <TextField
            select
            fullWidth
            label="Especialidad (opcional)"
            defaultValue=""
            error={!!profileForm.formState.errors.specialtyId}
            helperText={profileForm.formState.errors.specialtyId?.message}
            {...profileForm.register('specialtyId')}
            sx={fieldSx}
          >
            <MenuItem value="">Ninguna</MenuItem>
            {specialties?.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </TextField>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={onboardMutation.isPending}
            sx={{
              bgcolor: GREEN,
              borderRadius: '10px',
              py: 1.5,
              fontWeight: 700,
              fontSize: 14,
              textTransform: 'none',
              mt: 1,
              boxShadow: '0 4px 16px rgba(0,168,107,0.3)',
              '&:hover': { bgcolor: '#007F50', boxShadow: '0 6px 22px rgba(0,168,107,0.42)' },
            }}
          >
            {onboardMutation.isPending ? <CircularProgress size={20} color="inherit" /> : 'Completar Registro'}
          </Button>
        </Box>
      </FormPanel>
    </OnboardShell>
  );
};

// ─── Shell Layout (matches Login page split-panel) ───────────────────────────

const OnboardShell: React.FC<{ children: React.ReactNode; activeStep: number }> = ({ children, activeStep }) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      bgcolor: '#FFFFFF',
      '@keyframes fadeUp': {
        from: { opacity: 0, transform: 'translateY(22px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
      },
      '@keyframes fadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
      '@keyframes scaleIn': {
        from: { opacity: 0, transform: 'scale(0.7)' },
        to: { opacity: 1, transform: 'scale(1)' },
      },
    }}
  >
    {/* ── Left brand panel (desktop) ── */}
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        width: '42%',
        flexShrink: 0,
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: NAVY,
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.055) 1px, transparent 1px)',
        backgroundSize: '22px 22px',
        p: '48px 44px',
        animation: 'fadeIn 500ms ease both',
      }}
    >
      {/* Decorative circles */}
      <Box sx={{ position: 'absolute', top: -80, right: -80, width: 280, height: 280, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', bottom: -60, left: -40, width: 200, height: 200, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.03)', pointerEvents: 'none' }} />

      {/* Logo */}
      <Box>
        <img
          src="/logo-full.svg"
          alt="Lysi"
          style={{ height: 46, width: 'auto', filter: 'brightness(0) invert(1)', opacity: 0.92 }}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      </Box>

      {/* Step progress */}
      <Box>
        <Typography variant="h2" sx={{ color: '#FFFFFF', mb: 2.5 }}>
          Completa tu registro
        </Typography>
        <Box sx={{ width: 52, height: 3, borderRadius: 2, background: `linear-gradient(90deg, ${BLUE} 0%, ${GREEN} 100%)`, mb: 4 }} />

        {/* Steps */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {STEPS.map((s, i) => {
            const isActive = i === activeStep;
            const isDone = i < activeStep;
            return (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  transition: 'all 400ms ease',
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: isDone ? 'rgba(0,168,107,0.2)' : isActive ? 'rgba(0,102,204,0.2)' : 'rgba(255,255,255,0.06)',
                    color: isDone ? GREEN : isActive ? '#fff' : 'rgba(255,255,255,0.25)',
                    transition: 'all 400ms ease',
                    flexShrink: 0,
                  }}
                >
                  {isDone ? <CheckCircle sx={{ fontSize: 20 }} /> : React.cloneElement(s.icon, { sx: { fontSize: 20 } })}
                </Box>
                <Box>
                  <Typography sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: isActive ? '#fff' : isDone ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.3)',
                    transition: 'color 400ms ease',
                  }}>
                    {s.title}
                  </Typography>
                  <Typography sx={{
                    fontSize: 12,
                    color: isActive ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.2)',
                    transition: 'color 400ms ease',
                  }}>
                    {s.desc}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Footer */}
      <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: 11 }}>
        &copy; {new Date().getFullYear()} Lysi &middot; Plataforma de Gestión de Medicamentos
      </Typography>
    </Box>

    {/* ── Right form panel ── */}
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        px: { xs: 3, sm: 6, md: 8 },
        py: { xs: 4, md: 0 },
        position: 'relative',
      }}
    >
      {/* Mobile logo */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 4 }}>
        <img src="/logo-icon.svg" alt="Lysi" style={{ width: 48, height: 48 }} />
      </Box>

      {/* Mobile step indicator */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1, mb: 3, justifyContent: 'center' }}>
        {STEPS.map((_, i) => (
          <Box
            key={i}
            sx={{
              width: i === activeStep ? 32 : 12,
              height: 4,
              borderRadius: 2,
              bgcolor: i <= activeStep ? BLUE : '#E1E8ED',
              transition: 'width 300ms, background-color 300ms',
            }}
          />
        ))}
      </Box>

      <Box sx={{ width: '100%', maxWidth: 440 }}>
        {children}
      </Box>
    </Box>
  </Box>
);

// ─── Form Panel with transition ──────────────────────────────────────────────

const FormPanel: React.FC<{ children: React.ReactNode; visible: boolean }> = ({ children, visible }) => (
  <Box
    sx={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(16px)',
      transition: 'opacity 300ms ease, transform 300ms ease',
      animation: visible ? 'fadeUp 400ms ease both' : 'none',
    }}
  >
    {children}
  </Box>
);

// ─── Error Card (standalone, no shell) ───────────────────────────────────────

const ErrorCard: React.FC<{ message: string; sub?: string }> = ({ message, sub }) => (
  <Box
    sx={{
      minHeight: '100vh',
      bgcolor: '#EEF2F8',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 2,
    }}
  >
    <Box
      sx={{
        width: '100%',
        maxWidth: 420,
        bgcolor: '#fff',
        borderRadius: 4,
        p: { xs: 3, sm: 4 },
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        textAlign: 'center',
      }}
    >
      <img src="/logo-icon.svg" alt="Lysi" style={{ width: 48, height: 48, marginBottom: 16 }} />
      <Alert severity="error" sx={{ mb: 2, borderRadius: 2, textAlign: 'left' }}>{message}</Alert>
      {sub && <Typography sx={{ fontSize: 13, color: '#A0AEC0' }}>{sub}</Typography>}
    </Box>
  </Box>
);
